/**
 * The tag tree's arithmetic, on nothing but `{ id, parentId }`.
 *
 * Depth, height and descendants are asked for in three places — the worker
 * deciding whether a reparent is allowed, the panel refusing the same move
 * before it is sent, and the re-rooting of a shared bundle — and each had its
 * own walk over its own row shape. One walk here, over the two fields every
 * shape has, so the depth limit means the same thing on both sides of the wire.
 *
 * Pure. Rows whose parent is missing hang off the root; a parent cycle is
 * unreachable from the root and is measured honestly rather than looped over.
 */

/** The two fields a tree is made of. `null` (or absent) is a child of the root. */
export interface TreeRow {
  id: string;
  parentId?: string | null;
}

/** Rows indexed both ways, built once and asked many times. */
export interface TreeIndex<T extends TreeRow = TreeRow> {
  byId: Map<string, T>;
  childrenOf: Map<string | null, T[]>;
}

/** Either form is accepted; a list is indexed on the spot. */
export type TreeLike<T extends TreeRow = TreeRow> = TreeIndex<T> | readonly T[];

export function indexTree<T extends TreeRow>(rows: readonly T[]): TreeIndex<T> {
  const byId = new Map<string, T>();
  const childrenOf = new Map<string | null, T[]>();
  for (const row of rows ?? []) {
    if (!row || typeof row.id !== 'string') continue;
    byId.set(row.id, row);
    const key = row.parentId ?? null;
    const list = childrenOf.get(key);
    if (list) list.push(row);
    else childrenOf.set(key, [row]);
  }
  return { byId, childrenOf };
}

function asIndex<T extends TreeRow>(tree: TreeLike<T>): TreeIndex<T> {
  return Array.isArray(tree) ? indexTree(tree as readonly T[]) : (tree as TreeIndex<T>);
}

/**
 * How far below the root `id` sits: 0 for the root itself (`null`), 1 for a
 * top-level row, 2 for its child. A row whose parent chain loops has no depth
 * at all and answers `Infinity`, so a limit compared against it always refuses.
 */
export function depthOf<T extends TreeRow>(tree: TreeLike<T>, id: string | null): number {
  const { byId } = asIndex(tree);
  const seen = new Set<string>();
  let depth = 0;
  let cursor: string | null | undefined = id;
  while (cursor) {
    if (seen.has(cursor)) return Number.POSITIVE_INFINITY;
    seen.add(cursor);
    depth++;
    // A parent nobody has a row for is the root by another name.
    const parent: string | null = byId.get(cursor)?.parentId ?? null;
    cursor = parent !== null && byId.has(parent) ? parent : null;
  }
  return depth;
}

/** Levels below `id`: 0 for a leaf, 1 for a row with children, and so on. */
export function subtreeHeight<T extends TreeRow>(tree: TreeLike<T>, id: string): number {
  const { childrenOf } = asIndex(tree);
  const seen = new Set<string>([id]);
  const walk = (at: string): number => {
    let height = 0;
    for (const child of childrenOf.get(at) ?? []) {
      if (seen.has(child.id)) continue;
      seen.add(child.id);
      height = Math.max(height, 1 + walk(child.id));
    }
    return height;
  };
  return walk(id);
}

/** Every id strictly below `id`, in depth-first order of the rows given. */
export function descendantIds<T extends TreeRow>(tree: TreeLike<T>, id: string): Set<string> {
  const { childrenOf } = asIndex(tree);
  const out = new Set<string>();
  const walk = (at: string): void => {
    for (const child of childrenOf.get(at) ?? []) {
      if (out.has(child.id) || child.id === id) continue;
      out.add(child.id);
      walk(child.id);
    }
  };
  walk(id);
  return out;
}

/** True while `maybeAncestor` is strictly above `id`. A row is not its own ancestor. */
export function isAncestor<T extends TreeRow>(
  tree: TreeLike<T>,
  maybeAncestor: string,
  id: string,
): boolean {
  return maybeAncestor !== id && descendantIds(tree, maybeAncestor).has(id);
}
